import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  TextField,
  Switch,
  FormControlLabel,
  Stack,
  Typography,
  Divider,
  IconButton,
  Grid,
  Card,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  MenuItem,
  Menu,
  Tooltip,
} from "@mui/material";
import { Add, Delete, Visibility, Code, Image, FormatIndentIncrease } from "@mui/icons-material";
import { html as beautifyHtml } from "js-beautify";
import MDButton from "components/MDButton";
import { createEmailTemplate, updateEmailTemplate } from "api/endpoints/emailTemplates";
import {
  EMAIL_TEMPLATE_TYPES,
  DEFAULT_TEMPLATE_TYPE,
  mergeDeclarations,
  buildToken,
  renderSample,
} from "config/emailTemplatePresets";
import EmailImageDialog from "./EmailImageDialog";

// Derive the plain-text email body from the rich HTML — strips every tag and
// style. Block elements become line breaks, and links keep their URL as
// "text (url)" so text-only recipients can still reach them.
const htmlToPlainText = (html) => {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  doc.querySelectorAll("br").forEach((el) => el.replaceWith("\n"));
  doc.querySelectorAll("a[href]").forEach((el) => {
    const href = el.getAttribute("href");
    const text = el.textContent.trim();
    if (href && text && href !== text) el.textContent = `${text} (${href})`;
  });
  doc
    .querySelectorAll("p, div, h1, h2, h3, h4, h5, h6, li, tr, blockquote")
    .forEach((el) => el.append("\n"));
  return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
};

// A previously-saved template may store default/example wrapped as { name: ... };
// coerce back to a raw scalar so it does not feed an object into a TextField.
const toScalar = (val) => {
  if (val && typeof val === "object") return val.name ?? "";
  return val ?? "";
};

const EmailTemplateForm = ({ existingTemplate, websiteId, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    subject_template: "",
    html_template: "",
    text_template: "",
    active: true,
    variables: { version: "1.0", variables: [] },
  });

  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [templateType, setTemplateType] = useState(DEFAULT_TEMPLATE_TYPE);
  // { anchorEl, target } for the Insert-variable menu; target is "subject" | "body".
  const [menuState, setMenuState] = useState(null);

  const subjectInputRef = useRef(null);
  const rawHtmlInputRef = useRef(null);
  // { ref, pos } — caret to restore after a controlled-field token insert.
  const pendingCaretRef = useRef(null);

  // ✅ Load existing template
  useEffect(() => {
    if (existingTemplate && Object.keys(existingTemplate).length > 0) {
      setFormData({
        name: existingTemplate.name || "",
        subject_template: existingTemplate.subject_template || "",
        html_template: existingTemplate.html_template || "",
        text_template: existingTemplate.text_template || "",
        active: existingTemplate.active ?? true,
        variables: {
          version: existingTemplate.variables?.version || "1.0",
          variables: Array.isArray(existingTemplate.variables?.variables)
            ? existingTemplate.variables.variables.map((v) => ({
                ...v,
                default: toScalar(v.default),
                example: toScalar(v.example),
              }))
            : [],
        },
      });
      setTemplateType(existingTemplate.metadata?.template_type || DEFAULT_TEMPLATE_TYPE);
    }
  }, [existingTemplate]);

  // Restore the caret after a controlled-field insert — a re-rendered controlled
  // input otherwise jumps the caret to the end.
  useEffect(() => {
    const pending = pendingCaretRef.current;
    if (!pending) return undefined;
    pendingCaretRef.current = null;
    const id = requestAnimationFrame(() => {
      const el = pending.ref.current;
      if (el) {
        el.focus();
        el.setSelectionRange(pending.pos, pending.pos);
      }
    });
    return () => cancelAnimationFrame(id);
  }, [formData.subject_template, formData.html_template]);

  // Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTypeChange = (e) => {
    const next = e.target.value;
    setTemplateType(next);
    const declarations = EMAIL_TEMPLATE_TYPES[next]?.declarations || [];
    setFormData((prev) => ({
      ...prev,
      variables: {
        ...prev.variables,
        variables: mergeDeclarations(prev.variables?.variables || [], declarations),
      },
    }));
  };

  const handleVariableChange = (index, field, value) => {
    const updated = [...(formData.variables?.variables || [])];
    updated[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      variables: { ...prev.variables, variables: updated },
    }));
  };

  const addVariable = () => {
    setFormData((prev) => ({
      ...prev,
      variables: {
        ...prev.variables,
        variables: [
          ...(prev.variables?.variables || []),
          {
            name: "",
            type: "string",
            description: "",
            example: "",
            default: "",
            required: false,
          },
        ],
      },
    }));
  };

  const removeVariable = (index) => {
    const updated = [...(formData.variables?.variables || [])];
    updated.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      variables: { ...prev.variables, variables: updated },
    }));
  };

  // Splice a token into a controlled text field at its caret, then queue the
  // caret to be restored just past the inserted token.
  const insertIntoField = (fieldName, inputRef, token) => {
    const el = inputRef.current;
    const start = el ? el.selectionStart : null;
    const end = el ? el.selectionEnd : null;
    setFormData((prev) => {
      const cur = prev[fieldName] || "";
      const s = start ?? cur.length;
      const e = end ?? cur.length;
      pendingCaretRef.current = { ref: inputRef, pos: s + token.length };
      return { ...prev, [fieldName]: cur.slice(0, s) + token + cur.slice(e) };
    });
  };

  const handleInsertVariable = (entry) => {
    const target = menuState?.target;
    setMenuState(null);
    let token;
    if (entry.dynamic) {
      const field = window.prompt(
        "Submitted field name to insert.\n\nField keys depend on this form's schema — " +
          "a field a form does not collect simply renders empty."
      );
      if (!field || !field.trim()) return;
      token = buildToken(entry, field.trim());
    } else {
      token = buildToken(entry);
    }
    if (target === "subject") {
      insertIntoField("subject_template", subjectInputRef, token);
    } else {
      insertIntoField("html_template", rawHtmlInputRef, token);
    }
  };

  // Prettify the raw HTML on demand — js-beautify leaves {{ }} tokens intact.
  const handleFormatHtml = () =>
    setFormData((prev) => ({
      ...prev,
      html_template: beautifyHtml(prev.html_template || "", {
        indent_size: 2,
        wrap_line_length: 100,
        preserve_newlines: true,
        max_preserve_newlines: 2,
      }),
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject_template.trim() || !formData.html_template.trim()) {
      alert("Subject and HTML template are required.");
      return;
    }

    setLoading(true);
    try {
      // The plain-text template is always derived from the HTML on save —
      // there is no separate authored field, so the two never drift.
      const payload = {
        ...formData,
        text_template: htmlToPlainText(formData.html_template),
        metadata: { ...(existingTemplate?.metadata || {}), template_type: templateType },
      };
      if (existingTemplate?.id) {
        await updateEmailTemplate(websiteId, existingTemplate.id, payload);
      } else {
        await createEmailTemplate(websiteId, payload);
      }
      onSuccess();
    } catch (err) {
      alert("Error saving template");
    } finally {
      setLoading(false);
    }
  };

  const allVariables = formData.variables?.variables || [];
  const presetNames = new Set(
    (EMAIL_TEMPLATE_TYPES[templateType]?.declarations || []).map((d) => d.name)
  );
  const presetVariables = allVariables.filter((v) => presetNames.has(v.name));

  // Insert picker entries — the type's known leaves plus any custom variables.
  const pickerEntries = [
    ...(EMAIL_TEMPLATE_TYPES[templateType]?.tokens || []),
    ...allVariables
      .filter((v) => v.name && !presetNames.has(v.name))
      .map((v) =>
        v.type === "map"
          ? { label: v.name, base: v.name, dynamic: true }
          : { label: v.name, path: v.name }
      ),
  ];

  // path -> sample value, for the approximate Preview render.
  const sampleMap = (EMAIL_TEMPLATE_TYPES[templateType]?.tokens || []).reduce((acc, t) => {
    if (t.path && t.sample !== undefined) acc[t.path] = t.sample;
    return acc;
  }, {});

  const openMenu = (target) => (e) => setMenuState({ anchorEl: e.currentTarget, target });

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={3}>
        <TextField
          label="Template Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          fullWidth
          required
        />

        <TextField
          select
          label="Template Type"
          value={templateType}
          onChange={handleTypeChange}
          fullWidth
        >
          {Object.entries(EMAIL_TEMPLATE_TYPES).map(([key, def]) => (
            <MenuItem key={key} value={key}>
              {def.label}
            </MenuItem>
          ))}
        </TextField>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            label="Subject Template"
            name="subject_template"
            value={formData.subject_template}
            onChange={handleChange}
            inputRef={subjectInputRef}
            fullWidth
            required
          />
          <Tooltip title="Insert variable">
            <IconButton onClick={openMenu("subject")} sx={{ mt: 0.5 }} aria-label="Insert variable">
              <Code />
            </IconButton>
          </Tooltip>
        </Stack>

        {/* HTML Template Editor */}
        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            mb={1}
          >
            <Typography variant="subtitle1">HTML Template</Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                startIcon={<Image />}
                onClick={() => setImageDialogOpen(true)}
              >
                Add Image
              </MDButton>
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                startIcon={<FormatIndentIncrease />}
                onClick={handleFormatHtml}
              >
                Format
              </MDButton>
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                startIcon={<Code />}
                onClick={openMenu("body")}
              >
                Insert Variable
              </MDButton>
              <MDButton
                size="small"
                startIcon={<Visibility />}
                variant="contained"
                color="info"
                onClick={() => setPreviewOpen(true)}
              >
                Preview
              </MDButton>
            </Stack>
          </Stack>

          <TextField
            label="HTML Template"
            name="html_template"
            value={formData.html_template}
            onChange={handleChange}
            inputRef={rawHtmlInputRef}
            fullWidth
            multiline
            minRows={12}
            sx={{ "& textarea": { fontFamily: "monospace", fontSize: 13 } }}
          />
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={formData.active}
              onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
            />
          }
          label="Active"
        />

        <Divider />

        <Typography variant="h6">Variables</Typography>

        {templateType === "form_submission" && (
          <Typography variant="body2" color="text.secondary">
            ⚠️ <strong>submitted_data</strong> keys depend on the form that triggers this email — a
            field a given form does not collect simply renders empty.
          </Typography>
        )}

        {presetVariables.length > 0 && (
          <Stack spacing={1}>
            {presetVariables.map((v) => (
              <Box
                key={v.name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: 1,
                }}
              >
                <Typography variant="button" fontWeight="medium">
                  {v.name}
                </Typography>
                <Chip label={v.type} size="small" />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flex: 1, minWidth: 120 }}
                >
                  {v.description}
                </Typography>
                <Chip label="from template type" size="small" color="info" variant="outlined" />
              </Box>
            ))}
          </Stack>
        )}

        {allVariables.length === 0 && (
          <Typography variant="body2" color="text.secondary" mb={1}>
            No variables defined yet. Pick a template type above or add one below.
          </Typography>
        )}

        {allVariables.map((v, i) =>
          presetNames.has(v.name) ? null : (
            <Card key={i} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Name"
                    value={v.name}
                    onChange={(e) => handleVariableChange(i, "name", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Type"
                    value={v.type}
                    onChange={(e) => handleVariableChange(i, "type", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Default"
                    value={v.default || ""}
                    onChange={(e) => handleVariableChange(i, "default", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Description"
                    value={v.description}
                    onChange={(e) => handleVariableChange(i, "description", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Example"
                    value={v.example || ""}
                    onChange={(e) => handleVariableChange(i, "example", e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={v.required || false}
                        onChange={(e) => handleVariableChange(i, "required", e.target.checked)}
                      />
                    }
                    label="Required"
                  />
                </Grid>
              </Grid>
              <Box mt={1} textAlign="right">
                <IconButton onClick={() => removeVariable(i)} color="error">
                  <Delete />
                </IconButton>
              </Box>
            </Card>
          )
        )}

        <MDButton variant="outlined" color="info" startIcon={<Add />} onClick={addVariable}>
          Add Variable
        </MDButton>

        <Divider />

        <MDButton type="submit" color="info" variant="gradient" disabled={loading} fullWidth>
          {existingTemplate ? "Update Template" : "Create Template"}
        </MDButton>
      </Stack>

      {/* Insert-variable menu — shared by the Subject and Body buttons. */}
      <Menu
        anchorEl={menuState?.anchorEl}
        open={Boolean(menuState)}
        onClose={() => setMenuState(null)}
      >
        {pickerEntries.length === 0 ? (
          <MenuItem disabled>No variables — pick a type or add one</MenuItem>
        ) : (
          pickerEntries.map((entry, idx) => (
            <MenuItem key={`${entry.label}-${idx}`} onClick={() => handleInsertVariable(entry)}>
              {entry.label}
            </MenuItem>
          ))
        )}
      </Menu>

      {/* ✅ HTML Preview Dialog — tokens rendered with sample values. */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>HTML Template Preview</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" mb={1} display="block">
            Sample render — variables show placeholder values; conditional blocks are not evaluated.
          </Typography>
          <Box
            sx={{
              border: "1px solid #ccc",
              borderRadius: 1,
              overflow: "auto",
              backgroundColor: "#fff",
            }}
          >
            <iframe
              title="Email Preview"
              style={{
                width: "100%",
                height: "600px",
                border: "none",
                background: "#fff",
              }}
              srcDoc={renderSample(formData.html_template, sampleMap)}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <EmailImageDialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        websiteId={websiteId}
        onInsert={(tag) => insertIntoField("html_template", rawHtmlInputRef, tag)}
      />
    </Box>
  );
};

EmailTemplateForm.propTypes = {
  existingTemplate: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    subject_template: PropTypes.string,
    html_template: PropTypes.string,
    text_template: PropTypes.string,
    active: PropTypes.bool,
    metadata: PropTypes.object,
    variables: PropTypes.shape({
      version: PropTypes.string,
      variables: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          type: PropTypes.string,
          description: PropTypes.string,
          example: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
          default: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
          required: PropTypes.bool,
        })
      ),
    }),
  }),
  websiteId: PropTypes.string,
  onSuccess: PropTypes.func.isRequired,
};

EmailTemplateForm.defaultProps = {
  websiteId: undefined,
};

export default EmailTemplateForm;
